import { CheckCircle2, Info, MinusCircle, Quote } from "lucide-react";
import { KIT } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { ShoppingCheck } from "@/components/kit/ShoppingBits";

/** Sub-section opener: the section name, set plainly on the white reading surface. */
const Marker = ({ text }) => (
  <h2 className="pt-4 font-display text-[22px] font-semibold leading-snug text-brand-teal">{text}</h2>
);

const Ticks = ({ items, tone = "positive" }) => {
  const Icon = tone === "negative" ? MinusCircle : CheckCircle2;
  return (
    <ul className="grid gap-2.5">
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-start gap-3.5 text-[17px] leading-[1.75] text-brand-teal"
        >
          <Icon
            className={cn(
              "mt-0.5 h-[22px] w-[22px] shrink-0",
              tone === "negative" ? "text-brand-teal/35" : "text-brand-green"
            )}
            strokeWidth={2.25}
          />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

/** Numbered steps, same reading list as the item lists: coral number, then the step. */
const Steps = ({ items }) => (
  <ol className="divide-y divide-border">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-4 py-5 first:pt-1 last:pb-1">
        <span className="font-display text-[15px] font-semibold leading-[1.7] text-brand-orange">{i + 1}</span>
        <span className="text-[17px] leading-[1.7] text-[#1F2A28]">{item}</span>
      </li>
    ))}
  </ol>
);

/** Practical items read as a numbered reading list: coral number, bold item title, then the text,
 *  separated by hairlines. No tinted boxes, so a chapter stays one white reading surface. */
const Cards = ({ items }) => (
  <ol className="divide-y divide-border">
    {items.map((item, i) => {
      const at = item.indexOf(":");
      const lead = at > 0 ? item.slice(0, at) : null;
      const rest = at > 0 ? item.slice(at + 1).trim() : item;
      return (
        <li key={i} className="flex gap-4 py-5 first:pt-1 last:pb-1">
          <span className="font-display text-[15px] font-semibold leading-[1.7] text-brand-orange">{i + 1}</span>
          <div className="min-w-0 flex-1">
            {lead && (
              <p className="font-display text-[17px] font-bold leading-snug text-brand-teal">{lead}</p>
            )}
            <p className="mt-1 text-[17px] leading-[1.7] text-[#1F2A28]">{rest}</p>
          </div>
        </li>
      );
    })}
  </ol>
);

/** Three shopping methods, each its own rounded card: eyebrow, name, then what it covers.
 *  Authored as "EYEBROW | Name | Description" so any program can add its own set. */
const Methods = ({ items }) => (
  <div className="grid gap-3.5 sm:grid-cols-3">
    {items.map((item, i) => {
      const [eyebrow, name, text] = item.split("|").map((part) => part.trim());
      return (
        <div
          key={i}
          className="rounded-[20px] border border-brand-teal/10 bg-white p-6 shadow-[0_1px_2px_rgba(15,46,44,0.04),0_12px_26px_-18px_rgba(15,46,44,0.22)]"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-orangeInk">{eyebrow}</p>
          <p className="mt-2 font-display text-xl font-semibold leading-snug text-brand-teal">{name}</p>
          <p className="mt-2 text-[15px] leading-[1.65] text-brand-teal/75">{text}</p>
        </div>
      );
    })}
  </div>
);

const Callout = ({ text, tone }) => (
  <aside
    className={cn(
      "flex items-start gap-4 rounded-[20px] border-l-[5px] p-6",
      tone === "note"
        ? "border-brand-green bg-brand-green/[0.07]"
        : "border-brand-orange bg-brand-orange/[0.06]"
    )}
  >
    <Info
      className={cn("mt-0.5 h-6 w-6 shrink-0", tone === "note" ? "text-brand-green" : "text-brand-orange")}
    />
    <p className="whitespace-pre-line text-base leading-[1.75] text-brand-teal">{text}</p>
  </aside>
);

/** The one table every plan, grocery list and companion guide renders through.
 *  Desktop: a teal header, a white body separated by hairlines, the row label carrying the weight and
 *  the rest of the row set slightly back — the same hierarchy the meal cards use.
 *  Mobile: each row becomes its own little card, label above aligned label/value pairs.
 *  A worksheet-width table (more than four columns) keeps tighter gutters so nothing is clipped. */
/** Share of a shopping table given to the tick column. */
const TICK_WIDTH = 7;

const Table = ({ columns, rows, itemIds, shopping }) => {
  const count = columns?.length ?? rows?.[0]?.length ?? 0;
  const wide = count > 4;
  const pad = wide ? "px-3.5 py-4" : "px-6 py-[18px] sm:px-7";
  // A shoppable list gets a tick column; every other table renders exactly as before.
  const shoppable = !!itemIds?.length && !!shopping;
  const isChecked = (i) => shoppable && shopping.checked.includes(itemIds[i]);
  const tick = (i) => shopping.toggle(itemIds[i], !isChecked(i));
  // Typical text length per column, floored so an empty column still holds its place, then clamped so
  // no column collapses. Purely presentational: the content is never measured for anything else.
  const widths = (() => {
    if (wide || !count) return [];
    const weight = Array.from({ length: count }, (_, j) => {
      const lengths = (rows ?? []).map((r) => String(r?.[j] ?? "").length);
      const average = lengths.length ? lengths.reduce((a, b) => a + b, 0) / lengths.length : 0;
      // The row label is set in the wider display face, so it needs a little more room per character.
      const bias = j === 0 ? 1.25 : 1;
      return Math.max(average * bias, String(columns?.[j] ?? "").length * 0.7, 6);
    });
    const total = weight.reduce((a, b) => a + b, 0);
    const raw = weight.map((w) => Math.max((w / total) * 100, 100 / (count * 2.2)));
    const sum = raw.reduce((a, b) => a + b, 0);
    return raw.map((w) => Math.round((w / sum) * 1000) / 10);
  })();
  return (
    <div
      className={cn(
        "rounded-[20px] border border-brand-teal/10 bg-white shadow-[0_1px_2px_rgba(15,46,44,0.03),0_10px_26px_-20px_rgba(15,46,44,0.2)]",
        wide ? "overflow-x-auto sm:overflow-x-auto" : "overflow-hidden"
      )}
    >
      <table className={cn("hidden w-full table-fixed border-collapse sm:table", wide && "min-w-[640px] table-auto")}>
        {/* Column proportions derived from the content itself, so a long prose column gets the room
            and a short one (a quantity, a target, a gram figure) stays narrow — wherever it sits. */}
        {!wide && (
          <colgroup>
            {/* The tick column takes its share first; the content columns then keep exactly the
                proportions they had before checkboxes existed, scaled into what's left. */}
            {shoppable && <col style={{ width: `${TICK_WIDTH}%` }} />}
            {widths.map((w, j) => (
              <col key={j} style={{ width: `${shoppable ? (w * (100 - TICK_WIDTH)) / 100 : w}%` }} />
            ))}
          </colgroup>
        )}
        {columns && (
          <thead className="bg-brand-teal text-left">
            <tr>
              {shoppable && <th className="py-3.5 pl-5 pr-2" aria-label="Bought" />}
              {columns.map((c) => (
                <th
                  key={c}
                  className={cn(
                    "align-middle font-bold uppercase text-brand-cream/85",
                    pad,
                    "py-3.5",
                    wide
                      ? "whitespace-nowrap text-[10.5px] tracking-[0.1em]"
                      : "text-[11px] tracking-[0.16em]"
                  )}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className="divide-y divide-brand-teal/[0.07]">
          {rows?.map((row, i) => {
            const bought = isChecked(i);
            return (
              <tr
                key={i}
                data-testid={shoppable ? KIT.shopItem(itemIds[i]) : undefined}
                data-bought={shoppable ? bought : undefined}
                className={cn(
                  "transition-colors duration-150 hover:bg-brand-cream/60",
                  bought && "bg-brand-green/[0.04]"
                )}
              >
                {shoppable && (
                  <td className="align-top py-[18px] pl-5 pr-2">
                    <ShoppingCheck checked={bought} onChange={() => tick(i)} label={row[0]} />
                  </td>
                )}
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className={cn(
                      "align-top leading-[1.65]",
                      pad,
                      wide && "h-14",
                      j === 0
                        ? "font-display text-[16.5px] font-semibold text-brand-teal"
                        : "text-[15.5px] text-brand-teal/80",
                      // Ticked items stay exactly where they were — dimmed, never reordered.
                      bought && "text-brand-teal/40 line-through decoration-brand-teal/30"
                    )}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Mobile: one card per row — the label, then its values on their own aligned lines. */}
      <div className="divide-y divide-brand-teal/[0.07] sm:hidden">
        {rows?.map((row, i) => {
          const bought = isChecked(i);
          return (
            <div
              key={i}
              data-testid={shoppable ? `${KIT.shopItem(itemIds[i])}-mobile` : undefined}
              data-bought={shoppable ? bought : undefined}
              className={cn("px-5 py-[18px]", bought && "bg-brand-green/[0.04]")}
            >
              <div className={cn("flex items-start gap-3.5", shoppable || "block")}>
                {shoppable && (
                  <ShoppingCheck checked={bought} onChange={() => tick(i)} label={row[0]} />
                )}
                <div className="min-w-0 flex-1 space-y-2.5">
                  <p
                    className={cn(
                      "font-display text-[17px] font-semibold leading-snug text-brand-teal",
                      bought && "text-brand-teal/40 line-through decoration-brand-teal/30"
                    )}
                  >
                    {row[0]}
                  </p>
                  {row.slice(1).map((cell, j) => (
                    <div key={j} className="space-y-0.5">
                      {columns?.[j + 1] && (
                        <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-orangeInk">
                          {columns[j + 1]}
                        </p>
                      )}
                      <p
                        className={cn(
                          "min-h-[22px] text-[15.5px] leading-[1.6] text-brand-teal/80",
                          bought && "text-brand-teal/40 line-through decoration-brand-teal/30"
                        )}
                      >
                        {cell}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Pullquote = ({ text }) => (
  <blockquote className="relative rounded-[22px] bg-brand-green/[0.08] p-7 pl-16 sm:p-9 sm:pl-20">
    <Quote className="absolute left-6 top-7 h-8 w-8 text-brand-orange/70" fill="currentColor" strokeWidth={0} />
    <p className="font-display text-xl font-medium leading-[1.55] text-brand-teal sm:text-2xl">{text}</p>
  </blockquote>
);

/** One renderer, many personalities. `variant` is an optional authored presentation hint, so a new
 *  program gets tick-lists, steps, cards or tables with no new code — content stays exactly as authored. */
const Block = ({ block, shopping }) => {
  switch (block.type) {
    case "heading":
      return block.variant === "plain" ? (
        <h3 className="pt-2 font-display text-xl font-semibold text-brand-teal">{block.text}</h3>
      ) : (
        <Marker text={block.text} />
      );
    case "list":
      if (block.variant === "steps") return <Steps items={block.items ?? []} />;
      if (block.variant === "methods") return <Methods items={block.items ?? []} />;
      if (block.variant === "cards") return <Cards items={block.items ?? []} />;
      if (block.variant === "avoid") return <Ticks items={block.items ?? []} tone="negative" />;
      if (block.variant === "checklist") return <Ticks items={block.items ?? []} />;
      return (
        <ul className="grid gap-3.5">
          {block.items?.map((item, i) => (
            <li key={i} className="flex gap-3.5 text-base leading-[1.7] text-brand-teal">
              <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-orange" />
              {item}
            </li>
          ))}
        </ul>
      );
    case "callout":
      return <Callout text={block.text} tone={block.variant} />;
    case "quote":
      return <Pullquote text={block.text} />;
    case "table":
      return <Table columns={block.columns} rows={block.rows} itemIds={block.item_ids} shopping={shopping} />;
    case "image":
      return (
        <figure className="space-y-2">
          <img
            src={block.src}
            alt={block.caption ?? ""}
            loading="lazy"
            decoding="async"
            className="w-full rounded-[20px] object-cover"
          />
          {block.caption && <figcaption className="text-sm text-brand-teal/60">{block.caption}</figcaption>}
        </figure>
      );
    default:
      return (
        <p className="max-w-prose text-[17px] leading-[1.7] text-[#1F2A28]">{block.text}</p>
      );
  }
};

/** Renders any authored guide / intro page. Content-driven, zero bespoke code per program.
 *  `shopping` is optional: pass it and any table carrying `item_ids` becomes tickable. */
export const GuideSheet = ({ title, blocks = [], shopping }) => (
  <section data-testid={KIT.guideSheet} className="space-y-7">
    {title && (
      <h1 className="font-display text-3xl font-semibold leading-tight text-brand-teal sm:text-4xl">
        {title}
      </h1>
    )}
    {blocks.length ? (
      blocks.map((block, i) => <Block key={i} block={block} shopping={shopping} />)
    ) : (
      <p className="text-base text-brand-teal/60">Content for this section has not been published yet.</p>
    )}
  </section>
);
